import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  Divider
} from '@mui/material';
import { Storefront, Envelope, Phone, ChatCircleText } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import useThemeStyles from '../../../../theme/useThemeStyles';
import api from '../../../../utils/api';

const SuppliersSection = ({ suppliers, weddingId }) => {
  const styles = useThemeStyles();
  const navigate = useNavigate();

  const handleStartChat = async (supplierId) => {
    try {
      const response = await api.post('/api/chat/create', {
        type: 'supplier-supplier',
        secondBusinessId: supplierId,
        weddingId: weddingId
      });
      navigate(`/supplier/messages/${response.data._id}`);
    } catch (error) {
      alert('Failed to start chat. Please try again.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      venue: 'primary',
      photographer: 'secondary',
      caterer: 'success',
      florist: 'info',
      music: 'warning',
      planner: 'error'
    };
    return colors[category] || 'default';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!suppliers || suppliers.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Storefront size={24} style={{ marginRight: 12, color: '#64748b' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
            Suppliers
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
            No suppliers have access to this wedding yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Storefront size={24} style={{ marginRight: 12, color: '#64748b' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Suppliers ({suppliers.length})
        </Typography>
      </Box>

      <List>
        {suppliers.map((supplier, index) => (
          <React.Fragment key={supplier._id || index}>
            <ListItem
              sx={{
                py: 2,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {getInitials(supplier.businessName)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {supplier.businessName || 'Unknown Business'}
                    </Typography>
                    {supplier.category && (
                      <Chip
                        label={supplier.category}
                        size="small"
                        color={getCategoryColor(supplier.category)}
                        variant="outlined"
                      />
                    )}
                    {supplier.packageType && (
                      <Chip
                        label={supplier.packageType}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    {supplier.contactName && (
                      <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                        Contact: {supplier.contactName}
                      </Typography>
                    )}
                    {supplier.email && (
                      <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                        {supplier.email}
                      </Typography>
                    )}
                    {supplier.phone && (
                      <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                        {supplier.phone}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                {supplier.email && (
                  <Button
                    size="small"
                    startIcon={<Envelope size={16} />}
                    onClick={() => window.location.href = `mailto:${supplier.email}`}
                    variant="outlined"
                  >
                    Email
                  </Button>
                )}
                {supplier.phone && (
                  <Button
                    size="small"
                    startIcon={<Phone size={16} />}
                    onClick={() => window.location.href = `tel:${supplier.phone}`}
                    variant="outlined"
                  >
                    Call
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<ChatCircleText size={16} />}
                  onClick={() => handleStartChat(supplier._id)}
                  variant="contained"
                >
                  Message
                </Button>
              </Box>
            </ListItem>
            {index < suppliers.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default SuppliersSection;

